import { X, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  WORKFLOW_TEMPLATES,
  PENDING_TEMPLATE_KEY,
  type WorkflowTemplate,
} from "../templates/workflowTemplates";

interface TemplateGalleryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Gallery of starter workflow templates. Choosing one stashes its graph
 * skeleton in localStorage and opens the editor, which hydrates the nodes/edges
 * (via the same factory the "add node" button uses) for the user to review and save.
 */
export default function TemplateGalleryModal({ isOpen, onClose }: TemplateGalleryModalProps) {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const useTemplate = (tmpl: WorkflowTemplate) => {
    // Stash the skeleton for the editor to hydrate; clear any stale edit id so
    // saving creates a brand-new workflow rather than overwriting one.
    localStorage.setItem(
      PENDING_TEMPLATE_KEY,
      JSON.stringify({
        title: tmpl.title,
        // Load a SHORT subtitle into the editor header (the long card copy
        // overflows the header). Falls back to the category.
        description: tmpl.summary || tmpl.category,
        nodes: tmpl.nodes,
        edges: tmpl.edges,
      }),
    );
    localStorage.removeItem("template_id");
    navigate("/workflow/create");
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[88vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Start from a template</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Pick a starter pipeline — it opens in the editor pre-built for you to configure and save.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1" type="button">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Grid */}
        <div className="p-6 overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-4">
          {WORKFLOW_TEMPLATES.map((tmpl) => {
            const Icon = tmpl.icon;
            return (
              <button
                key={tmpl.key}
                onClick={() => useTemplate(tmpl)}
                type="button"
                className="text-left bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all group"
              >
                <div className="flex items-start gap-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex-shrink-0">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-semibold text-gray-900 text-sm truncate">{tmpl.title}</h3>
                      <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400 flex-shrink-0">
                        {tmpl.category}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">{tmpl.description}</p>

                    {/* Step chips */}
                    <div className="flex flex-wrap items-center gap-1 mt-3">
                      {tmpl.nodes.map((n, i) => (
                        <span key={n.id} className="inline-flex items-center">
                          <span className="text-[11px] text-gray-600 bg-gray-100 rounded px-1.5 py-0.5">
                            {n.label}
                          </span>
                          {i < tmpl.nodes.length - 1 && (
                            <ArrowRight className="w-3 h-3 text-gray-300 mx-0.5" />
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="mt-3 text-xs font-medium text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                  Use this template →
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
